import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Skeleton } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import styles from './styles.module.css';
import { Line } from 'react-chartjs-2';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import SalesReportsService from 'services/SalesReportsService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import UpgradeToPremiumMessage from 'components/utils/UpgradeToPremiumMessage';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import AnalyticsHelper from 'helpers/AnalyticsHelper';
import { CLARITY_EVENTS, SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import Chart from 'chart.js/auto';
import { CategoryScale } from 'chart.js';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Menu, MenuItem, useMediaQuery, useTheme, Typography } from '@mui/material';
import ShopsHelper from 'helpers/ShopsHelper';
import SuccessDialog from 'components/popups/SuccessDialog';
import { useTranslation } from 'react-i18next';
import CustomDateFilterDialog from 'components/popups/CustomDateFilterDialog';


Chart.register(CategoryScale);

const TIME_RANGE = {
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  SIX_MONTHS: 'six-months',
  CUSTOM: 'custom',
};

function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');

  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const handleFilterSelect = (range) => {
    if (range === TIME_RANGE.CUSTOM) {
      setCustomDialogOpen(true);
    } else {
      setFilterDateRange(range);
    }
    handleFilterClose();
  };

  const [filterDateRange, setFilterDateRange] = useState(TIME_RANGE.TODAY);
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [salesGraphData, setSalesGraphData] = useState({
    labels: [],
    datasets: [
      {
        label: t('Sales graph'),
        backgroundColor: '#605ca8',
        borderColor: '#605ca8',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: '#605ca8',
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 3,
        pointHoverRadius: 5,
        data: [],
      },
    ],
  });
  const [summaryReport, setSummaryReport] = useState({});
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);

  const updateByDateResponseData = useCallback(
    (data) => {
      if (data.length === 1) data = [{ date: '', amount: 0 }, ...data];
      setSalesGraphData({
        labels: data.map((sale) => sale.date),
        datasets: [
          {
            label: t('Sales graph'),
            backgroundColor: '#605ca8',
            borderColor: '#605ca8',
            borderWidth: 2,
            tension: 0.4,
            fill: false,
            pointBackgroundColor: '#605ca8',
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            pointRadius: 3,
            pointHoverRadius: 5,
            data: data.map((sale) => sale.amount),
          },
        ],
      });
    },
    [t]
  );

  const dateRangeMap = {
    [TIME_RANGE.TODAY]: DateTimeHelpers.getTodayStrInYMD(),
    [TIME_RANGE.WEEK]: DateTimeHelpers.getWeekStr(),
    [TIME_RANGE.MONTH]: DateTimeHelpers.getMonthStr(),
    [TIME_RANGE.SIX_MONTHS]: DateTimeHelpers.getSixMonthStr(),
  };

  const _getDateRangeData = useCallback((_dateRange, customFrom, customTo) => {
    if (_dateRange === TIME_RANGE.CUSTOM) {
      if (!customFrom || !customTo) return null;
      return {
        from_date: DateTimeHelpers.getDateYMD(customFrom),
        to_date: DateTimeHelpers.getDateYMD(customTo),
      };
    }
    const from_date = dateRangeMap[_dateRange] || '';
    return { from_date, to_date: DateTimeHelpers.getTodayStrInYMD() };
    // eslint-disable-next-line
  }, []);

  const _hasReportsGeneratePermission = useCallback(() => {
    return ((user || {}).shop_permissions || {}).reports_permission === USER_PERMISSIONS.WRITE;
  }, [user]);

  const loadReports = useCallback(
    async (_dateRange, customFrom, customTo, isInitialLoad = false) => {
      if (!_hasReportsGeneratePermission()) {
        return;
      }

      if (!isInitialLoad) {
        setRangeLoading(_dateRange);
      }
      setIsChartLoading(true);
      setErrorMessage('');

      try {
        const data = _getDateRangeData(_dateRange, customFrom, customTo);
        if (!data) return;

        const fetchChartData = async () => {
          try {
            const salesByDate = await SalesReportsService.getSalesByDate(data);
            updateByDateResponseData(salesByDate.reverse());
          } finally {
            setIsChartLoading(false);
          }
        };

        const fetchSummaryData = async () => {
          try {
            const summaryReport = await SalesReportsService.getSalesSummary(data);
            setSummaryReport(summaryReport);
          } finally {
            setIsSummaryLoading(false);
          }
        };

        setIsChartLoading(true);
        setIsSummaryLoading(true);

        await Promise.all([fetchChartData(), fetchSummaryData()]);
      } catch (err) {
        console.error(err.message);
        setErrorMessage(t('Failed to load sales data. Please try again later.'));
        setIsChartLoading(false);
        setIsSummaryLoading(false);
      } finally {
        setRangeLoading(null);
      }
    },
    [_getDateRangeData, _hasReportsGeneratePermission, updateByDateResponseData, t]
  );

  useEffect(() => {
    const isInitial = !salesGraphData.labels.length;
    if (isInitial) {
      AnalyticsHelper.event(CLARITY_EVENTS.LOAD_HOME_PAGE);
    }
    loadReports(filterDateRange, customFromDate, customToDate, isInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDateRange, customFromDate, customToDate, loadReports]);

  if (!_hasReportsGeneratePermission()) {
    return <>{user ? <div>{tCommon('You do not have permission to generate reports')}</div> : <div>Loading...</div>}</>;
  }

  const subscriptionType = shop?.subscription?.type || '';
  const isSummaryLoadingFull = isSummaryLoading;
  const isChartLoadingFull = isChartLoading;

  return (
    <>
      <div className={styles.contentWrapper}>
        <div className={styles.headerSec}>
          <Typography variant="h5" className={styles.pageTitle}>
            {t('Sales')} <span className={styles.subTitle}>{t('Dashboard')}</span>
            {filterDateRange === TIME_RANGE.CUSTOM && customFromDate && customToDate && (
              <span className={styles.customDateSub}>
                {` (${DateTimeHelpers.convertDateToDMY(customFromDate)} - ${DateTimeHelpers.convertDateToDMY(customToDate)})`}
              </span>
            )}
          </Typography>

          <div className={styles.filterSection}>
            {isMobile ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FilterAltIcon />}
                  onClick={handleFilterClick}
                  className={styles.addButtonStyle}
                  size="small"
                >
                  {t('Filter')}
                </Button>
                <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
                  {Object.values(TIME_RANGE).map((range) => (
                    <MenuItem
                      key={range}
                      selected={filterDateRange === range}
                      onClick={() => handleFilterSelect(range)}
                    >
                      {t(range.toUpperCase().replace('-', '_'))}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              Object.values(TIME_RANGE).map((range) => (
                <Button
                  key={range}
                  variant={filterDateRange === range ? 'contained' : 'outlined'}
                  color="primary"
                  disabled={rangeLoading !== null}
                  onClick={() =>{ if (range === TIME_RANGE.CUSTOM) {
                    setCustomDialogOpen(true);
                  } else {
                    setFilterDateRange(range);
                  }}
                }
                  className={`${styles.addButtonStyle} ${filterDateRange === range ? styles.activeFilter : ''}`}
                  size="small"
                >
                  {rangeLoading === range ? tCommon('Loading...') : t(range.toUpperCase().replace('-', '_'))}
                </Button>
              ))
            )}
          </div>
        </div>

        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <div
              className={styles.priceCard}
              style={{ backgroundColor: '#77ACF1' }}
            >
              <h2 className={styles.price}>
                {isSummaryLoadingFull ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={45}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                  />
                ) : (
                  <>
                    {shop?.currency || ''}
                    <span className={styles.count}>
                      {ShopsHelper.getAmountFormatted(shop, summaryReport.total_amount || 0.0)}
                    </span>
                  </>
                )}
              </h2>
              <h3 className={styles.summaryLabelBig}>{t('Total sales')}</h3>
              <div className={styles.iconSec}>
                <ShoppingCartIcon className={styles.icon} />
              </div>
              <div className={styles.invoicesDetails}>
                <h3 className={styles.category}>
                  {isSummaryLoadingFull ? (
                    <Skeleton variant="text" width={40} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    summaryReport.no_of_invoices || 0
                  )}
                </h3>
                <h3 className={styles.category}>{t('Invoices')}</h3>
              </div>
            </div>
          </div>
          <div className={styles.summaryItem}>
            <div
              className={styles.priceCard}
              style={{ backgroundColor: '#FF7F00' }}
            >
              <h2 className={styles.price}>
                {isSummaryLoadingFull ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={45}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                  />
                ) : (
                  <>
                    {shop?.currency || ''}
                    <span className={styles.count}>
                      {ShopsHelper.getAmountFormatted(shop, summaryReport.total_cost || 0.0)}
                    </span>
                  </>
                )}
              </h2>
              <h3 className={styles.summaryLabelBig}>{t('Total cost')}</h3>
              <div className={styles.iconSec}>
                <DescriptionIcon className={styles.icon} />
              </div>
              <div className={styles.invoicesDetails}>
                <h3 className={styles.category}>
                  {isSummaryLoadingFull ? (
                    <Skeleton variant="text" width={80} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    <>
                      {shop?.currency || ''}
                      <span className={styles.count}>
                        {ShopsHelper.getAmountFormatted(shop, summaryReport.total_tax || 0.0)}
                      </span>
                    </>
                  )}
                </h3>
                <h3 className={styles.category}>{t('Purchase tax')}</h3>
              </div>
              {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM && (
                <div className={styles.coverParentFull}>
                  <UpgradeToPremiumMessage />
                </div>
              )}
            </div>
          </div>
          <div className={styles.summaryItem}>
            <div
              className={styles.priceCard}
              style={{ backgroundColor: '#3fc425ff' }}
            >
              <h2 className={styles.price}>
                {isSummaryLoadingFull ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={45}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                  />
                ) : (
                  <>
                    {shop?.currency || ''}
                    <span className={styles.count}>
                      {ShopsHelper.getAmountFormatted(shop, summaryReport.total_sales_tax || 0.0)}
                    </span>
                  </>
                )}
              </h2>
              <h3 className={styles.summaryLabelBig}>{t('Tax')}</h3>
              <div className={styles.iconSec}>
                <DescriptionIcon className={styles.icon} />
              </div>
              <div className={styles.invoicesDetails}>
                <h3 className={styles.category}>
                  {isSummaryLoadingFull ? (
                    <Skeleton variant="text" width={80} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    <>
                      {shop?.currency || ''}
                      <span className={styles.count}>
                        {ShopsHelper.getAmountFormatted(shop, summaryReport.total_sales_cess || 0.0)}
                      </span>
                    </>
                  )}
                </h3>
                <h3 className={styles.category}>{t('Additional Tax')}</h3>
              </div>
            </div>
          </div>
          <div className={styles.summaryItem}>
            <div
              className={styles.priceCard}
              style={{ backgroundColor: '#00C0EF' }}
            >
              <h2 className={styles.price}>
                {isSummaryLoadingFull ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={45}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                  />
                ) : (
                  <>
                    {shop?.currency || ''}
                    <span className={styles.count}>
                      {ShopsHelper.getAmountFormatted(shop, summaryReport.profit || 0.0)}
                    </span>
                  </>
                )}
              </h2>
              <h3 className={styles.summaryLabelBig}>{t('Profit')}</h3>
              <div className={styles.iconSec}>
                <LocalOfferIcon className={styles.icon} />
              </div>
              <div className={styles.invoicesDetails}>
                <h3 className={styles.category}>
                  {isSummaryLoadingFull ? (
                    <Skeleton variant="text" width={80} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    <>
                      {shop?.currency || ''}
                      <span className={styles.count}>
                        {ShopsHelper.getAmountFormatted(shop, summaryReport.damage_value || 0.0)}
                      </span>
                    </>
                  )}
                </h3>
                <h3 className={styles.category}>{t('Damage value')}</h3>
              </div>
              {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM && (
                <div className={styles.coverParentFull}>
                  <UpgradeToPremiumMessage />
                </div>
              )}
            </div>
          </div>
          <div className={styles.summaryItem}>
            <div
              className={styles.priceCard}
              style={{ backgroundColor: '#ff0068' }}
            >
              <h2 className={styles.price}>
                {isSummaryLoadingFull ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={45}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                  />
                ) : (
                  <>
                    {shop?.currency || ''}
                    <span className={styles.count}>
                      {ShopsHelper.getAmountFormatted(shop, summaryReport.total_discount || 0.0)}
                    </span>
                  </>
                )}
              </h2>
              <h3 className={styles.summaryLabelBig}>{t('Discount')}</h3>
              <div className={styles.iconSec}>
                <FlashOnIcon className={styles.icon} />
              </div>
              {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.PREMIUM && (
                <div className={styles.invoicesDetails}>
                  <h3 className={styles.category}>
                    {isSummaryLoadingFull ? (
                      <Skeleton variant="text" width={80} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                    ) : (
                      <>
                        {shop?.currency || ''}
                        <span className={styles.count}>
                          {ShopsHelper.getAmountFormatted(shop, summaryReport.total_expense || 0.0)}
                        </span>
                      </>
                    )}
                  </h3>
                  <h3 className={styles.category}>{t('Expense')}</h3>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.changeable}>
          {errorMessage && (
            <div className={styles.errorMessage}>
              <p>{errorMessage}</p>
            </div>
          )}
          <div className={styles.secTitle}>
            <h2 className={styles.chartTitle}>{t('Sales overview chart')}</h2>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {isChartLoadingFull && !rangeLoading ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                animation="wave"
                sx={{ borderRadius: '16px', minHeight: 240 }}
              />
            ) : (
              <div style={{ flex: 1, position: 'relative', width: '100%', minHeight: 240 }}>
                <Line
                  data={salesGraphData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                      y: {
                        ticks: {
                          maxTicksLimit: 5,
                        },
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)',
                        },
                      },
                    },
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {showSuccessDialog && <SuccessDialog onClose={() => setShowSuccessDialog(false)} />}
      <CustomDateFilterDialog
        open={customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
        onApply={(fromDate, toDate) => {
          setCustomFromDate(fromDate);
          setCustomToDate(toDate);
          setFilterDateRange(TIME_RANGE.CUSTOM);
          setCustomDialogOpen(false);
        }}
        initialFromDate={customFromDate}
        initialToDate={customToDate}
      />
    </>
  );
}

export default withConsoleBase(Dashboard);
